import {fetchActiveCare, fetchOwnedSession} from '@/intake/portalApi'

export const LEGACY_CONTINUATION_CONTEXT = 'ESTABLISHED_LEGACY_CONTINUATION'

function positiveId(raw) {
  const value = typeof raw === 'string' || typeof raw === 'number' ? String(raw) : ''
  if (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)) || Number(value) <= 0) {
    throw new Error('CARE_SESSION_ID_INVALID')
  }
  return String(Number(value))
}

function explicitSessionId(context) {
  let selected = null
  for (const key of ['id', 'sessionId', 'user_session_id', 'userSessionId']) {
    if (!Object.prototype.hasOwnProperty.call(context, key)) continue
    const value = positiveId(context[key])
    if (selected !== null && selected !== value) throw new Error('CARE_SESSION_ID_INVALID')
    selected = value
  }
  return selected
}

async function ownedSession(id) {
  const session = await fetchOwnedSession(id)
  if (!session || String(session.id) !== id) throw new Error('SESSION_RESPONSE_INVALID')
  return session
}

/** Preparation is advisory; only the admission endpoint can issue room access. */
export async function resolveSessionPreparation(context = {}) {
  const id = explicitSessionId(context || {})
  if (id !== null) {
    const session = await ownedSession(id)
    if (session.state === 'date_fixed' && session.admission_context === LEGACY_CONTINUATION_CONTEXT) {
      return {session, care: null, legacy: true}
    }
    // A missing care episode is never evidence of a legacy relationship.
    const care = await fetchActiveCare({id})
    return {session, care, legacy: false}
  }

  // Preserve the existing no-ID V2 entry using only the server's active care
  // anchor. Never choose an arbitrary legacy session from history or cache.
  const care = await fetchActiveCare()
  if (!care || !care.first_user_session_id) return {session: null, care, legacy: false}
  const session = await ownedSession(positiveId(care.first_user_session_id))
  return {session, care, legacy: false}
}

export function sessionPreparationReady({session, care, legacy}) {
  return !!(session && session.state === 'date_fixed'
    && ((legacy === true && session.admission_context === LEGACY_CONTINUATION_CONTEXT)
      || (care && care.first_session_operational_readiness
        && care.first_session_operational_readiness.state === 'READY')))
}

/** Support navigation records a reference, never care/consent or admission authority. */
export function legacySessionSupportRoute(session, lang, returnTo) {
  if (!session || session.state !== 'date_fixed' || session.admission_context !== LEGACY_CONTINUATION_CONTEXT
      || !['wait', 'room'].includes(returnTo)) return null
  try {
    return {name: 'portalRequestNew', params: {lang}, query: {
      from: 'session-access', topic: 'ACCESS', session_id: positiveId(session.id), return_to: returnTo,
    }}
  } catch (_) { return null }
}

export function sessionAccessSupportContext(query = {}) {
  if (query.from !== 'session-access' || !['wait', 'room'].includes(query.return_to)) return null
  try { return {id: positiveId(query.session_id), returnTo: query.return_to} }
  catch (_) { return null }
}
